import {
    createElement,
    getAttributes,
    insertElements,
    prerenderElementsTree,
} from './../render-api';
import type {
    Attributes,
    ElementChild,
    ElementChildren,
    ElementConfig,
} from '../../components/ui-element.type';
import {
    isNode,
    isElementNode,
    isElementConfig,
    isDocumentFragment,
} from '../helpers';
import { GlobalIgnoredMutationAttributes, MutationOperation } from './index';
import type {
    UIElementMutationCallback,
    SmoothAppendCallback,
    SmoothRemoveCallback,
} from './index';
import { Maybe, NullOr } from '../utils.type';
import { buildKeyMap } from './mutations.helper';

/**
 * Mutates attributes object.
 * @memberof UI
 * @alias mutateAttributesTo
 * @param {Record<string, string>} oldAttributes
 * @param {Record<string, string>} newAttributes
 * @returns {Function}
 */
const mutateAttributesTo = (
    oldAttributes: Attributes,
    newAttributes: Attributes
): UIElementMutationCallback => {
    const patches: Array<UIElementMutationCallback> = [];
    const ignoredAttributes = [...GlobalIgnoredMutationAttributes];
    if (oldAttributes['data-transition']) {
        ignoredAttributes.push('class', 'data-transition');
    }

    for (const oldKey in oldAttributes) {
        if (!oldAttributes.hasOwnProperty(oldKey)) {
            continue;
        }
        if (ignoredAttributes.includes(oldKey)) {
            continue;
        }
        if (!(oldKey in newAttributes)) {
            patches.push(
                (function (attr: string) {
                    return (element: Node | HTMLElement): void => {
                        if (isElementNode(element)) {
                            element.removeAttribute(attr);
                        }
                    };
                })(oldKey)
            );
        }
    }

    for (const key in newAttributes) {
        const noOrIgnoredAttribute =
            !newAttributes.hasOwnProperty(key) ||
            ignoredAttributes.includes(key);
        if (noOrIgnoredAttribute) {
            continue;
        }
        if (newAttributes[key] !== oldAttributes[key]) {
            patches.push(
                (function (attr: string) {
                    return (element: Node | HTMLElement): void => {
                        const attributeValue = newAttributes[attr];

                        const isEmptyAttribute =
                            attributeValue === null ||
                            typeof attributeValue === 'undefined';
                        if (isEmptyAttribute && isElementNode(element)) {
                            element.removeAttribute(attr);
                            return;
                        }

                        switch (typeof attributeValue) {
                            case 'boolean':
                                if (isElementNode(element)) {
                                    attributeValue
                                        ? element.setAttribute(attr, '')
                                        : element.removeAttribute(attr);
                                }
                                break;
                            case 'object':
                                // Skip object attributes - they're handled differently
                                break;
                            default:
                                if (isElementNode(element)) {
                                    element.setAttribute(
                                        attr,
                                        String(attributeValue)
                                    );
                                }
                        }
                    };
                })(key)
            );
        }
    }

    return (element: Node | HTMLElement): void =>
        patches.forEach((patch) => patch(element));
};

/**
 * It will create a path to mutate old element to new element.
 * @memberof UI
 * @alias mutateTo
 * @param {Node} oldElement
 * @param {Node} newElement
 * @param {boolean} [immediately] - indicate if patch should be applied immediately
 * @returns {Function}
 */
const mutateTo = (
    oldElement: Node,
    newElement: Node,
    immediately: boolean = false
): UIElementMutationCallback => {
    const shouldReplaceElement =
        isElementNode(oldElement) && isElementNode(newElement)
            ? oldElement.localName !== newElement.localName
            : oldElement.nodeType !== newElement.nodeType ||
              oldElement.nodeValue !== newElement.nodeValue;

    if (shouldReplaceElement) {
        return (element: Node | HTMLElement): NullOr<Node> => {
            // Very rare edge case when host element is empty
            if (isElementNode(element) && element.children.length === 0) {
                element.appendChild(newElement);
            }

            const parentElement = element.parentElement;
            if (parentElement) {
                parentElement.insertBefore(newElement, element);
                parentElement.removeChild(element);
                return newElement;
            }
            return null;
        };
    }

    const patches: Array<UIElementMutationCallback> = [];

    if (isElementNode(oldElement) && isElementNode(newElement)) {
        patches.push(
            mutateAttributesTo(
                getAttributes(oldElement),
                getAttributes(newElement)
            ),
            mutateChildrenTo(
                Array.from(oldElement.childNodes),
                Array.from(newElement.childNodes)
            )
        );
    }
    const result: UIElementMutationCallback = (
        element: Node | HTMLElement
    ): void => {
        patches.forEach((patch) => patch(element));
    };

    if (immediately) {
        prerenderElementsTree(newElement, true);
        result(oldElement);
    }

    return result;
};

/**
 * Mutates children object.
 * @memberof UI
 * @alias mutateChildrenTo
 * @param {Array<HTMLElement> | NodeListOf<HTMLElement>} oldChildren
 * @param {Array<HTMLElement> | NodeListOf<HTMLElement>} newChildren
 * @param {Function} [smoothRemove]
 * @param {Function} [smoothAppend]
 * @returns {Function}
 */
const mutateChildrenTo = (
    oldChildren: Array<Node>,
    newChildren: Array<Node>,
    smoothRemove?: SmoothRemoveCallback,
    smoothAppend?: SmoothAppendCallback
): UIElementMutationCallback => {
    const patches: Array<UIElementMutationCallback> = [];

    const oldKeyMap = buildKeyMap(oldChildren);
    const reusedOldElements = new Set<Node>();
    const elementOperations: Array<{
        type: MutationOperation;
        oldElement?: Node;
        newElement?: Node;
        newIndex?: number;
    }> = [];

    // Process newChildren - determine what to do with each new element
    newChildren.forEach((newElement, newIndex) => {
        // Skip null/undefined elements in case they are there
        if (!newElement) {
            return;
        }

        const key = isElementNode(newElement)
            ? newElement.getAttribute('key')
            : null;

        if (key?.trim() && oldKeyMap.has(key)) {
            // Case 1: Element has a key and matches an old element - REUSE
            const { element: oldElement } = oldKeyMap.get(key)!;
            reusedOldElements.add(oldElement);
            elementOperations.push({
                type: MutationOperation.Reuse,
                oldElement,
                newElement,
                newIndex,
            });
        } else {
            // Case 2: Check if this element matches any old element (by reference or structure)
            let matchingOldElement: Node | null = null;
            let matchingIndex = -1;

            // First try exact reference match
            matchingIndex = oldChildren.indexOf(newElement);
            if (matchingIndex !== -1) {
                matchingOldElement = newElement; // Same reference
            } else {
                if (isElementNode(newElement)) {
                    for (let i = 0; i < oldChildren.length; i++) {
                        const oldChild = oldChildren[i];
                        const areElementsMatching =
                            isElementNode(oldChild) &&
                            !reusedOldElements.has(oldChild) &&
                            oldChild.localName === newElement.localName &&
                            !oldChild.getAttribute('key') &&
                            !newElement.getAttribute('key');
                        if (areElementsMatching) {
                            matchingOldElement = oldChild;
                            matchingIndex = i;
                            break;
                        }
                    }
                }
            }

            const isMatchingOldElementAndNotReused =
                matchingOldElement &&
                !reusedOldElements.has(matchingOldElement);
            if (isMatchingOldElementAndNotReused) {
                // Found a matching element - Reuse
                reusedOldElements.add(matchingOldElement!);
                elementOperations.push({
                    type: MutationOperation.Reuse,
                    // ! - we are sure it's not null here, check above
                    oldElement: matchingOldElement!,
                    newElement: newElement,
                    newIndex,
                });
            } else {
                // Case 3: New element or no match found - Add
                elementOperations.push({
                    type: MutationOperation.Add,
                    newElement,
                    newIndex,
                });
            }
        }
    });

    oldChildren.forEach((oldElement) => {
        if (oldElement && !reusedOldElements.has(oldElement)) {
            // Case 4: Old element not reused - Remove
            elementOperations.push({
                type: MutationOperation.Remove,
                oldElement,
            });
        }
    });

    elementOperations.forEach((operation) => {
        switch (operation.type) {
            case MutationOperation.Add:
                if (
                    operation.newElement &&
                    typeof operation.newIndex !== 'undefined'
                ) {
                    patches.push((parentElement: Node | HTMLElement): void => {
                        const referenceNode =
                            parentElement.childNodes[operation.newIndex!] ||
                            null;
                        if (smoothAppend) {
                            smoothAppend(operation.newElement!, parentElement);
                        } else {
                            parentElement.insertBefore(
                                operation.newElement!,
                                referenceNode
                            );
                        }
                    });
                }
                break;

            case MutationOperation.Remove:
                if (operation.oldElement) {
                    patches.push((parentElement: Node | HTMLElement): void => {
                        if (
                            !operation.oldElement ||
                            !parentElement ||
                            operation.oldElement.parentElement !== parentElement
                        ) {
                            return;
                        }
                        if (smoothRemove) {
                            smoothRemove(operation.oldElement);
                        } else {
                            parentElement.removeChild(operation.oldElement);
                        }
                    });
                }
                break;

            case MutationOperation.Reuse:
                if (
                    operation.oldElement &&
                    operation.newElement &&
                    typeof operation.newIndex !== 'undefined'
                ) {
                    patches.push((parentElement: Node | HTMLElement): void => {
                        const oldElement = operation.oldElement;
                        const newElement = operation.newElement!;
                        const targetIndex = operation.newIndex!;

                        let currentIndex = -1;
                        if (oldElement && isElementNode(oldElement)) {
                            currentIndex = Array.from(
                                parentElement.childNodes
                            ).indexOf(oldElement);
                        }
                        if (
                            currentIndex !== targetIndex &&
                            currentIndex !== -1
                        ) {
                            const referenceNode =
                                parentElement.childNodes[targetIndex] || null;
                            parentElement.insertBefore(
                                oldElement!,
                                referenceNode
                            );
                        }

                        // Only mutate if it's not the exact same element instance
                        const isMatchingElement =
                            oldElement !== newElement &&
                            oldElement &&
                            isElementNode(oldElement);
                        if (isMatchingElement) {
                            mutateTo(oldElement, newElement)(oldElement);
                        }
                    });
                }
                break;
        }
    });

    return (element: Node | HTMLElement): void => {
        patches.forEach((patch) => patch(element));
    };
};

/**
 * @memberof UI
 * @alias rebuildChildren
 * @param {HTMLElement} targetElement
 * @param {Array<ElementConfig | ElementChild | Node> | ElementChildren | DocumentFragment} source
 * @param {Function} [smoothRemove]
 * @param {Function} [smoothAppend]
 * @returns {HTMLElement}
 */
const rebuildChildren = (
    targetElement: HTMLElement,
    source:
        | Array<ElementConfig | ElementChild | Node>
        | ElementChildren
        | DocumentFragment,
    smoothRemove?: SmoothAppendCallback,
    smoothAppend?: SmoothAppendCallback
): HTMLElement => {
    const elements = Array.from(
        isDocumentFragment(source) ? source.childNodes : source
    );
    const newElements = elements.reduce<Array<HTMLElement>>(
        (
            collection: Array<HTMLElement>,
            entry: Node | ElementChild | ElementConfig
        ) => {
            return collection.concat(
                [entry].map((item: Node | ElementChild | ElementConfig) => {
                    if (isElementConfig(item)) {
                        const element = createElement<HTMLElement>(item)!;
                        prerenderElementsTree(element, true);
                        return element;
                    }
                    return entry as HTMLElement;
                })
            );
        },
        []
    );

    const oldElements: Array<HTMLElement> = [].filter.call(
        targetElement.childNodes,
        (node: ChildNode | HTMLElement): boolean => {
            return isElementNode(node) && !node.classList.contains('-removing');
        }
    );
    if (!oldElements.length) {
        insertElements(targetElement, newElements);
    } else {
        mutateChildrenTo(
            oldElements,
            newElements,
            smoothRemove,
            smoothAppend
        )(targetElement);
    }

    return targetElement;
};

/**
 * @memberof UI
 * @alias rebuild
 * @param {HTMLElement} element
 * @param {ElementConfig | Node} newElement
 * @returns {Node}
 */
const rebuild = (
    element: Node,
    newElement: ElementConfig | Node
): NullOr<Node> => {
    const targetElement = isNode(newElement)
        ? newElement
        : createElement(newElement);
    prerenderElementsTree(targetElement as HTMLElement, true);
    const mutation = mutateTo(element, targetElement as HTMLElement, false);
    const maybeReplacement = mutation(element) as Maybe<void>;
    return maybeReplacement ?? element;
};

export {
    mutateTo,
    mutateChildrenTo,
    mutateAttributesTo,
    rebuildChildren,
    rebuild,
};
